import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  Colors,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import styles from './line-chart.module.css';

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Legend, Tooltip, Colors, ChartDataLabels);

interface Props {
  data: ChartData<'line'>;
  showDataLabels?: boolean;
}

const LineChart = ({ data, showDataLabels = false }: Props) => {
  const isDataLonger = useMemo(
    () =>
      data.datasets.length &&
      data.datasets.some(dataset => dataset.data.some(count => typeof count === 'number' && count > 8)),
    [data.datasets],
  );

  const maxCount = useMemo(() => {
    const allValues = (data.datasets ?? []).flatMap(dataset => dataset.data ?? []);
    const numericValues = allValues.map(value => Number(value)).filter(value => !Number.isNaN(value));
    return Math.max(0, ...numericValues);
  }, [data.datasets]);

  return (
    <div className={styles.lineChartWrapper}>
      <Line
        data={data}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                font: {
                  weight: 'bold',
                },
              },
            },
            datalabels: {
              display: context => showDataLabels && context.dataset.data[context.dataIndex] !== 0,
              color: '#35404F',
              backgroundColor: '#F9F9F9',
              borderRadius: 4,
              anchor: 'end',
              align: 'end',

              padding: {
                top: 4,
                bottom: 4,
                right: 8,
                left: 8,
              },
              formatter: value => value || '',
            },
          },
          scales: {
            y: {
              min: 0,
              ...(!isDataLonger && { max: maxCount < 6 ? 6 : maxCount + 1 }),
            },
            x: {
              offset: true,
            },
          },
        }}
      />
    </div>
  );
};

export default LineChart;
